import {
  ResumePetisSimulationProposalClientRequest,
  ResumePetisSimulationProposalClientResponse,
} from '../../@types/ResumePetisSimulationProposal'
import { usePaymentStore } from '../../Stores/paymentStore'
import { useReportStore } from '../../Stores/reportStore'
import { useResumePetisSimulationProposalStore } from '../../Stores/useResumePetisSimulationProposalStore'
import {
  DisabledStepsByResume,
  useResumeStore,
} from '../../Stores/useResumeStore'
import { INVOICE_STATUS } from '../../constants/invoiceStatus'
import { ROUTES } from '../../constants/routes'
import { SESSION_STORAGE } from '../../constants/sessionStorage'
import {
  resumeRedirectRouter,
  resumeStep,
} from '../../utils/resumeRedirectRouter'
import { referenceNumberResumeType } from '../../utils/validates'
import { usePaymentConfirmationStore } from './../../Stores/paymentConfirmationStore'
import { useAnimalsHydration } from './animalsHydration/useAnimalsHydration'
import { useCreateProposalHydration } from './proposalHydration/useCreateProposalHydration'
import { useSubmitProposalHydration } from './proposalHydration/useSubmitProposalHydration'
import { useQuotationHydration } from './quotationHydration/useQuotationHydration'
import { useRunPetisSimulationHydration } from './runPetisSimulationHydration/useRunPetisSimulationHydration'
import { useAcceptConditionsHydration } from './useAcceptConditionsHydration'
import { useDocumentsToListHydration } from './useDocumentsToListHydration'
import { useFirstReceiptInfoHydration } from './useFirstReceiptInfoHydration'
import { useGetEntitiesHydration } from './useGetEntitiesHydration'
import { useNavigateRouteResume } from './useNavigateRouteResume'
import { usePolicyHolderHydration } from './usePolicyHolderHydration'
import { useProspectHydration } from './useProspectHydration'
import { useRefreshDigitalSignatureStatusHydration } from './useRefreshDigitalSignatureStatusHydration'
import { useSendDigitalSignatureHydration } from './useSendDigitalSignatureHydration'
import { useSimulationHydration } from './useSimulationHydration'

type HandleInitialResumeType = {
  data: ResumePetisSimulationProposalClientResponse
  variables: ResumePetisSimulationProposalClientRequest
}

export const useInitialResume = () => {
  const { setDirectDebit } = usePaymentStore()
  const {
    setIsResume,
    setQuotationResume,
    setResumeType,
    setDisabledStepsByResume,
  } = useResumeStore()
  const { setReportGenerated, setIsReportGeneratedEnabled } = useReportStore()
  const { handleResumeNavigate } = useNavigateRouteResume()
  const { setIsFirstReceiptPaid } = usePaymentConfirmationStore()
  const { setResumePetisSimulationProposal } =
    useResumePetisSimulationProposalStore()
  const { handleEntitiesHydration } = useGetEntitiesHydration()
  const { handleSubmitProposalHydration } = useSubmitProposalHydration()
  const { handleSendDigitalSignatureHydration } =
    useSendDigitalSignatureHydration()
  const {
    handlePolicyholderDetailsSelectedHydration,
    handlePolicyholderAdditionalDetails,
  } = usePolicyHolderHydration()
  const { handleAnimalsHydration } = useAnimalsHydration()
  const { handleCreateProposalHydration } = useCreateProposalHydration()
  const { handleRequestQuotationHydration, handleGetQuotationHydration } =
    useQuotationHydration()
  const { handleRunPetisSimulationHydrate } = useRunPetisSimulationHydration()
  const { handleAcceptConditionsHydration } = useAcceptConditionsHydration()
  const { handleFirstReceiptInfoHydration } = useFirstReceiptInfoHydration()
  const { handleDocumentsToListHydration } = useDocumentsToListHydration()
  const { handleProspectHydration } = useProspectHydration()
  const { handleSimulationHydration } = useSimulationHydration()
  const { handleRefreshDigitalSignatureStatusHydration } =
    useRefreshDigitalSignatureStatusHydration()

  const handleNavigate = (
    data: ResumePetisSimulationProposalClientResponse,
  ) => {
    const route = resumeRedirectRouter(data.redirectionStepCode!) || ROUTES.base

    const currentResumeStep: number = resumeStep(data.redirectionStepCode!) || 1
    let stepsToDisabledByResume = {} as DisabledStepsByResume

    for (let i = currentResumeStep - 1; i >= 1; i--) {
      if (
        data.redirectionStepCode === '10' ||
        data.redirectionStepCode === '20'
      ) {
        stepsToDisabledByResume = {
          ...stepsToDisabledByResume,
          step1: false,
        }
      } else {
        stepsToDisabledByResume = {
          ...stepsToDisabledByResume,
          [`step${i}`]: true,
        }
      }
    }

    setDisabledStepsByResume(stepsToDisabledByResume)
    handleResumeNavigate(route)
  }

  const handleInitialResume = ({
    data,
    variables,
  }: HandleInitialResumeType) => {
    const isProposal = data.processData?.proposalData
    const isPolicy = data.processData?.policyData
    const proposalOrPolicyData = isProposal || isPolicy
    const _resumeType = referenceNumberResumeType(variables.requestId!)
    const isDirectDebitRunPetis =
      data.processData?.simulationData?.runPetisSimulationClientResponse
        ?.runPetisSimulationResponse?.isDebitDirect
    const isDebitDirect =
      //data.redirectionStepCode === '20' ||
      proposalOrPolicyData?.isDirectDebit ||
      isDirectDebitRunPetis
        ? '1'
        : '0'

    sessionStorage.setItem(
      SESSION_STORAGE.oldRpsQuoteId,
      data.processData?.simulationData?.runPetisSimulationClientResponse
        ?.runPetisSimulationResponse?.quoteId || '',
    )

    handleAnimalsHydration(data)

    setResumePetisSimulationProposal(data)
    setIsResume(true)
    setResumeType(_resumeType)
    setQuotationResume(Boolean(data.processData?.quotationData))
    setDirectDebit(isDebitDirect)

    handleNavigate(data)

    if (data.entities?.policyHolder?.idEntity) {
      //SET Entities/PolicyHolder
      handleEntitiesHydration(data)
      handlePolicyholderDetailsSelectedHydration(data)
      handlePolicyholderAdditionalDetails(data)
    }

    if (data.entities?.prospectEntity) {
      handleProspectHydration(data)
    }

    handleRunPetisSimulationHydrate(data)
    handleCreateProposalHydration(data)
    handleDocumentsToListHydration(data)
    handleSubmitProposalHydration(data)
    handleAcceptConditionsHydration(data)
    handleFirstReceiptInfoHydration(data)
    handleSimulationHydration(data)

    //STEP 5 Quotation
    handleGetQuotationHydration(data)
    handleRequestQuotationHydration(data)
    handleSendDigitalSignatureHydration(data)

    //STEP 7 Digital Signature
    handleRefreshDigitalSignatureStatusHydration(data)

    if (data.processData?.firstReceipt?.invoiceStatus) {
      setIsFirstReceiptPaid(
        data.processData?.firstReceipt?.invoiceStatus === INVOICE_STATUS.PAID,
      )
    }

    if ((data.processData?.documentList || [])?.length > 0) {
      setReportGenerated(true)
      setIsReportGeneratedEnabled(false)
    }
  }

  return {
    handleInitialResume,
  }
}
